import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Plug, Save, Check, ToggleLeft, ToggleRight, Key, Sparkles, AlertCircle,
  Loader2, Shield, CreditCard, Mail, Database, ExternalLink, Lock, ChevronDown, ChevronUp,
} from 'lucide-react';

const ICONS = {
  sparkles: Sparkles,
  'credit-card': CreditCard,
  mail: Mail,
  database: Database,
  shield: Shield,
};

function GuideCard({ item, isDark, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const Icon = ICONS[item.icon] || Plug;

  return (
    <article
      className={`rounded-xl border overflow-hidden ${isDark ? 'border-white/10 bg-[#151820]' : 'border-gray-200 bg-white'}`}
      data-testid={`integration-guide-${item.id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-start gap-3 p-4 text-left ${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-gray-50'}`}
      >
        <div className={`shrink-0 p-2 rounded-lg ${item.editable ? 'bg-red-950/40 text-red-400' : 'bg-white/5 text-gray-400'}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.title}</h3>
            {item.editable ? (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-600/20 text-red-300 border border-red-500/30">
                Configurable ici
              </span>
            ) : (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/5 text-gray-500 border border-white/10">
                Serveur uniquement
              </span>
            )}
          </div>
          <p className={`text-xs mt-1 leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{item.summary}</p>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-500 shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />}
      </button>

      {open && (
        <div className={`px-4 pb-4 space-y-4 border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
          {item.id === 'suppliers' ? (
            <div className="space-y-3">
              <p className={`text-xs leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Les identifiants Mobilax, JensMobiles et LCD-Phone ont une page dédiée (accessible aux admins).
              </p>
              <Link
                to="/admin/identifiants-fournisseurs"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold bg-cyan-600 hover:bg-cyan-500 text-white"
              >
                <Key className="w-4 h-4" />
                Ouvrir Identifiants fournisseurs
              </Link>
            </div>
          ) : null}

          {item.id !== 'suppliers' && (
          <div className={`rounded-lg p-3 text-xs leading-relaxed ${isDark ? 'bg-black/25 text-gray-300' : 'bg-gray-50 text-gray-700'}`}>
            <p className={`font-semibold uppercase tracking-wide mb-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Où va la clé</p>
            <p>{item.storage}</p>
            {item.env_vars?.length > 0 && (
              <p className="mt-2 font-mono text-[11px] text-emerald-400/90">
                {item.env_vars.join(' · ')}
              </p>
            )}
          </div>
          )}

          {item.get_key_url && (
            <a
              href={item.get_key_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300"
            >
              Obtenir une clé sur le portail fournisseur
              <ExternalLink className="w-3 h-3" />
            </a>
          )}

          <div>
            <p className={`text-[10px] font-bold uppercase tracking-wide mb-2 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
              Fonctions qui utilisent cette clé
            </p>
            <ul className="space-y-2">
              {(item.used_by || []).map((u) => (
                <li
                  key={`${u.feature}-${u.endpoint}`}
                  className={`rounded-lg p-3 text-xs ${isDark ? 'bg-white/[0.02] border border-white/5' : 'bg-gray-50 border border-gray-100'}`}
                >
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <strong className={isDark ? 'text-white' : 'text-gray-900'}>{u.feature}</strong>
                    {u.page && u.page !== '—' && (
                      <Link to={u.page} className="text-red-400 hover:underline font-mono">{u.page}</Link>
                    )}
                  </div>
                  <p className={`font-mono text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{u.endpoint}</p>
                  {u.detail && <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{u.detail}</p>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </article>
  );
}

export function IntegrationApisPanel({ variant = 'dark' }) {
  const { api, user } = useAuth();
  const isDark = variant === 'dark';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [enabled, setEnabled] = useState(false);
  const [provider, setProvider] = useState('openai');
  const [modelChat, setModelChat] = useState('gpt-4o-mini');
  const [modelVision, setModelVision] = useState('gpt-4o');
  const [apiKey, setApiKey] = useState('');

  const fetchSettings = async () => {
    try {
      const res = await api.get('/admin/integration-apis');
      setData(res.data);
      const llm = res.data.llm || {};
      setEnabled(!!llm.enabled);
      setProvider(llm.provider || 'openai');
      setModelChat(llm.model_chat || 'gpt-4o-mini');
      setModelVision(llm.model_vision || 'gpt-4o');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, [api]);

  const handleSave = async () => {
    setSaving(true);
    setTestResult(null);
    try {
      const payload = {
        llm_enabled: enabled,
        llm_provider: provider,
        llm_model_chat: modelChat,
        llm_model_vision: modelVision,
      };
      if (apiKey.trim()) payload.llm_api_key = apiKey.trim();
      const res = await api.put('/admin/integration-apis', payload);
      setData(res.data);
      setApiKey('');
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post('/admin/integration-apis/llm/test');
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ ok: false, detail: err.response?.data?.detail || 'Erreur réseau' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
      </div>
    );
  }

  const llm = data?.llm || {};
  const guide = data?.guide || [];
  const readOnlyGuide = guide.filter((g) => !g.editable);

  const inputCls = isDark
    ? 'bg-black/30 border-white/10 text-white'
    : 'bg-gray-50 border-gray-200 text-gray-900';

  return (
    <div className="space-y-6" data-testid="integration-apis-panel">
      <div className={`rounded-xl border p-4 flex gap-3 ${isDark ? 'border-amber-500/20 bg-amber-950/20' : 'border-amber-200 bg-amber-50'}`}>
        <Lock className={`w-5 h-5 shrink-0 mt-0.5 ${isDark ? 'text-amber-400' : 'text-amber-700'}`} />
        <div className={`text-sm leading-relaxed ${isDark ? 'text-amber-100/90' : 'text-amber-900'}`}>
          <strong>Page réservée à Carlos uniquement.</strong> Connecté en tant que{' '}
          <code className="font-mono text-xs">{user?.username}</code>. Les clés saisies ici sont chiffrées
          en base MongoDB — elles ne sont jamais renvoyées en clair à l&apos;écran après enregistrement.
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className={`text-xl font-bold flex items-center gap-2 uppercase tracking-wide ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Key className="w-5 h-5 text-red-400" />
            Clés API & intégrations
          </h1>
          <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            Comprendre où va chaque clé · configurer OpenAI pour l&apos;import facture
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleTest}
            disabled={testing || !enabled}
            className={`min-h-[44px] flex items-center gap-2 px-4 rounded-lg text-sm font-semibold disabled:opacity-40 border ${isDark ? 'border-white/10 bg-white/5 text-gray-200 hover:bg-white/10' : 'border-gray-200 bg-gray-100 text-gray-700'}`}
            data-testid="integration-test-btn"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Tester OpenAI
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="min-h-[44px] flex items-center gap-2 px-4 rounded-lg text-sm font-bold bg-red-600 hover:bg-red-500 text-white disabled:opacity-50"
            data-testid="integration-save-btn"
          >
            {saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
            {saved ? 'Enregistré' : saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>

      {testResult && (
        <div className={`rounded-xl p-4 border text-sm ${testResult.ok ? 'bg-emerald-950/30 border-emerald-500/30 text-emerald-300' : 'bg-red-950/30 border-red-500/30 text-red-300'}`}>
          {testResult.ok ? (
            <p><strong>OK —</strong> {testResult.sample}</p>
          ) : (
            <p className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              {testResult.detail || 'Échec du test'}
            </p>
          )}
        </div>
      )}

      {/* OpenAI — editable */}
      <section className={`rounded-xl border p-5 space-y-4 ${isDark ? 'border-red-500/20 bg-[#151820]' : 'border-red-200 bg-white'}`} data-testid="openai-config-section">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className={`text-base font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <Sparkles className="w-5 h-5 text-red-400" />
              OpenAI — saisir la clé ici
            </h2>
            <p className={`text-xs mt-2 max-w-2xl leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Collez votre clé <code className="text-red-400">sk-proj-…</code> obtenue sur{' '}
              <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-red-400 hover:underline">
                platform.openai.com
              </a>
              . Activez le toggle, enregistrez, puis testez. Sans clé valide,{' '}
              <Link to="/stock/import-facture" className="text-red-400 hover:underline">Import facture IA</Link>{' '}
              affichera « IA non configurée ».
            </p>
          </div>
          <button type="button" onClick={() => setEnabled((v) => !v)} className="p-1 shrink-0" aria-label="Activer ou désactiver l'IA">
            {enabled ? <ToggleRight className="w-10 h-10 text-red-500" /> : <ToggleLeft className="w-10 h-10 text-gray-500" />}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1 uppercase font-semibold">Fournisseur</label>
            <select value={provider} onChange={(e) => setProvider(e.target.value)} className={`w-full border rounded-lg px-3 py-2.5 text-sm min-h-[44px] ${inputCls}`}>
              <option value="openai">OpenAI</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1 uppercase font-semibold flex items-center gap-1">
              <Key className="w-3 h-3" /> Clé API OpenAI
            </label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={llm.has_api_key ? 'Laisser vide = conserver la clé actuelle' : 'sk-proj-…'}
              className={`w-full border rounded-lg px-3 py-2.5 text-sm min-h-[44px] ${inputCls}`}
              autoComplete="off"
              data-testid="openai-api-key-input"
            />
            {llm.api_key_masked && (
              <p className="text-xs mt-1 text-gray-500">
                Clé enregistrée : <span className="font-mono text-emerald-400">{llm.api_key_masked}</span>
                {llm.uses_env_fallback && ' · secours variable serveur OPENAI_API_KEY'}
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1 uppercase font-semibold">Modèle texte (emails, chat)</label>
            <input type="text" value={modelChat} onChange={(e) => setModelChat(e.target.value)} className={`w-full border rounded-lg px-3 py-2.5 text-sm min-h-[44px] ${inputCls}`} placeholder="gpt-4o-mini" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1 uppercase font-semibold">Modèle vision (factures PDF)</label>
            <input type="text" value={modelVision} onChange={(e) => setModelVision(e.target.value)} className={`w-full border rounded-lg px-3 py-2.5 text-sm min-h-[44px] ${inputCls}`} placeholder="gpt-4o" />
          </div>
        </div>

        {data?.updated_at && (
          <p className="text-xs text-gray-600">
            Dernière modification : {new Date(data.updated_at).toLocaleString('fr-FR')}
            {data.updated_by ? ` · ${data.updated_by}` : ''}
          </p>
        )}
      </section>

      {/* Full guide */}
      <section className="space-y-3">
        <h2 className={`text-xs font-bold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
          Référence — toutes les intégrations
        </h2>
        {guide.filter((g) => g.editable).map((item) => (
          <GuideCard
            key={item.id}
            item={item}
            isDark={isDark}
            defaultOpen={item.id === 'suppliers'}
          />
        ))}
        {readOnlyGuide.map((item) => (
          <GuideCard key={item.id} item={item} isDark={isDark} defaultOpen={false} />
        ))}
      </section>

      <div className={`rounded-xl p-4 text-xs leading-relaxed border ${isDark ? 'bg-white/[0.02] border-white/10 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
        <p className="flex items-start gap-2">
          <Shield className="w-4 h-4 shrink-0 text-gray-500 mt-0.5" />
          Les clés « Serveur uniquement » (Stripe, SumUp, Mongo…) sont modifiées par le développeur directement
          sur le VPS dans <code className="font-mono">backend/.env.preview.local</code>, puis redémarrage du backend.
          OpenAI et les identifiants fournisseurs se gèrent sur des pages dédiées (
          <Link to="/admin/cles-api" className="text-cyan-400 hover:underline">Clés API</Link>
          {' · '}
          <Link to="/admin/identifiants-fournisseurs" className="text-cyan-400 hover:underline">Identifiants fournisseurs</Link>
          ).
        </p>
      </div>
    </div>
  );
}

export default IntegrationApisPanel;
